import {
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  type LucideIcon,
} from "lucide-react";
import { useGetList, useLocaleState, useTranslate } from "ra-core";
import { cn } from "@/lib/utils";

import { useGetSalesName } from "../sales/useGetSalesName";
import type { Call } from "../types";
import { CallButton } from "./CallButton";
import { CallPlayer } from "./CallPlayer";
import { callKind, formatDuration } from "./callFormat";

export type CallsFilter =
  | { contact_id: Call["contact_id"] }
  | { lead_id: Call["lead_id"] }
  | { deal_id: Call["deal_id"] };

const ICONS: Record<ReturnType<typeof callKind>, LucideIcon> = {
  incoming: PhoneIncoming,
  outgoing: PhoneOutgoing,
  missed: PhoneMissed,
};

const PER_PAGE = 50;

/** The calls of a contact, a lead or a deal, newest first (who sees them: crm/db/14_calls.sql). */
export const CallsList = ({ filter }: { filter: CallsFilter }) => {
  const translate = useTranslate();
  const { data, total, isPending, error } = useGetList<Call>("calls", {
    filter,
    sort: { field: "started_at", order: "DESC" },
    pagination: { page: 1, perPage: PER_PAGE },
  });
  if (isPending) return null;
  if (error || !data?.length) {
    return (
      <p className="text-sm text-muted-foreground py-1">
        {translate("crm.calls.empty")}
      </p>
    );
  }
  return (
    <>
      <ul
        className="flex flex-col divide-y"
        aria-label={translate("crm.calls.title")}
      >
        {data.map((call) => (
          <CallRow key={call.id} call={call} />
        ))}
      </ul>
      {total != null && total > data.length ? (
        <p className="text-xs text-muted-foreground pt-2">
          {translate("crm.calls.only_latest", { smart_count: data.length })}
        </p>
      ) : null}
    </>
  );
};

const CallRow = ({ call }: { call: Call }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const kind = callKind(call);
  const Icon = ICONS[kind];
  const who = useGetSalesName(call.sales_id ?? undefined, {
    enabled: call.sales_id != null,
  });
  const when = new Date(call.started_at).toLocaleString(locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  const label = translate(`crm.calls.kind.${kind}`);
  return (
    <li className="flex items-center gap-3 py-2 min-w-0" data-call={call.id}>
      <Icon
        className={cn(
          "size-4 shrink-0",
          kind === "missed" ? "text-destructive" : "text-muted-foreground",
        )}
        aria-label={label}
        role="img"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{when}</div>
        <div className="text-xs text-muted-foreground truncate">
          {label} ·{" "}
          {call.sales_id != null ? who : translate("crm.calls.nobody")}
          {call.status !== "success" && kind !== "missed"
            ? ` · ${translate(`crm.calls.status.${call.status}`)}`
            : ""}
        </div>
      </div>
      {call.status === "success" ? (
        <span className="text-sm tabular-nums shrink-0">
          {formatDuration(call.duration)}
        </span>
      ) : null}
      <CallPlayer url={call.record_url} />
    </li>
  );
};

/** «Звонки» with «Позвонить» for a card. */
export const CallsSection = ({
  filter,
  phone,
  className,
}: {
  filter: CallsFilter;
  phone?: string | null;
  className?: string;
}) => {
  const translate = useTranslate();
  return (
    <section className={cn("flex flex-col gap-2 min-w-0", className)}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-medium">{translate("crm.calls.title")}</h3>
        <CallButton phone={phone} />
      </div>
      <CallsList filter={filter} />
    </section>
  );
};
